import { retrieveRawInitData } from '@telegram-apps/bridge';

import { apiBaseUrl } from '../constants';
import {
  CreateSubscriptionPaymentResponse,
  GetMuxTokensResponse,
  GetRootResponse,
  GetCourseContentsResponse,
  GetCourseLandingResponse,
  UpdateCourseProgressRequest,
} from './contract';

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Wrap fetch with synthetic delays in development
const originalFetch = globalThis.fetch;
const wrappedFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  // TODO: also throw artificial errors
  const delayMs = Math.floor(Math.random() * (3000 - 150 + 1)) + 150;
  console.debug(`Delaying fetch for ${delayMs}ms`);
  await delay(delayMs);
  return originalFetch(input, init);
};

// Bind fetch to wrapped version in current scope only in development
const fetch = import.meta.env.DEV ? wrappedFetch : originalFetch;

export const fetchMuxTokens = async (courseId: string, sectionId: string, lectureId: string, signal?: AbortSignal): Promise<GetMuxTokensResponse["data"]> => {
  try {
    const initData = retrieveRawInitData();
    const res = await fetch(
      `${apiBaseUrl}/courses/${courseId}/sections/${sectionId}/lectures/${lectureId}/mux-tokens`,
      {
        headers: { Authorization: `tma ${initData}` },
        signal,
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    const j = await res.json() as GetMuxTokensResponse;
    return j.data;
  } catch (error) {
    console.error("Failed to fetch Mux tokens:", error);
    throw error;
  }
};

export const fetchInitialData = async (signal?: AbortSignal): Promise<GetRootResponse["data"]> => {
  try {
    const initData = retrieveRawInitData();
    const res = await fetch(
      apiBaseUrl,
      {
        headers: { Authorization: `tma ${initData}` },
        signal,
      }
    );
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) throw new Error(`🚫`);
      throw new Error(`Failed to load data (${res.status})`);
    }
    const j = await res.json() as GetRootResponse;
    return j.data ?? null;
  } catch (error) {
    console.error("Failed to fetch initial data:", error);
    throw error;
  }
};

export const fetchCourseContents = async (courseId: string, signal?: AbortSignal): Promise<GetCourseContentsResponse["data"]> => {
  try {
    const initData = retrieveRawInitData();
    const res = await fetch(
      `${apiBaseUrl}/courses/${courseId}/contents`,
      {
        headers: { Authorization: `tma ${initData}` },
        signal,
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    const j = await res.json() as GetCourseContentsResponse;
    return j.data;
  } catch (error) {
    console.error("Failed to fetch course contents:", error);
    throw error;
  }
};

export const fetchCourseLanding = async (courseId: string, signal?: AbortSignal): Promise<GetCourseLandingResponse["data"]> => {
  try {
    const initData = retrieveRawInitData();
    const res = await fetch(
      `${apiBaseUrl}/courses/${courseId}/landing`,
      {
        headers: { Authorization: `tma ${initData}` },
        signal,
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    const j = await res.json() as GetCourseLandingResponse;
    return j.data;
  } catch (error) {
    console.error("Failed to fetch course landing:", error);
    throw error;
  }
};

export const createSubscriptionPayment = async (courseId: string, currency: string, signal?: AbortSignal): Promise<string> => {
  try {
    const initData = retrieveRawInitData();
    const res = await fetch(
      `${apiBaseUrl}/courses/${courseId}/subscription-payments`,
      {
        headers: {
          Authorization: `tma ${initData}`,
          'Content-Type': 'application/json'
        },
        method: 'POST',
        body: JSON.stringify({ currency }),
        signal,
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    const j = await res.json() as CreateSubscriptionPaymentResponse;
    return j.data.tgInvoiceLink;
  } catch (error) {
    console.error("Failed to create subscription payment:", error);
    throw error;
  }
};

export const updateCourseProgress = async (courseId: string, progressUpdates: UpdateCourseProgressRequest): Promise<void> => {
  try {
    const initData = retrieveRawInitData();
    const res = await fetch(
      `${apiBaseUrl}/courses/${courseId}/progress`,
      {
        method: 'POST',
        headers: {
          'Authorization': `tma ${initData}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(progressUpdates),
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
  } catch (error) {
    console.error("Failed to update course progress:", error);
    throw error;
  }
};
